import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Star, Medal, Lightning, Crown, Camera, ChatCircle, Ticket, Target, InstagramLogo, Share, Code, Copy, WhatsappLogo } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PromoFullscreen } from "@/components/PromoSlider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIZES_IMG = "https://static.prod-images.emergentagent.com/jobs/77c7f82e-9496-46da-8450-a0e1e07f0b18/images/b151dac9fc77bcb801630d6822f5816ab8eed45db80bcf44760a7fb6ee8d9c8a.png";

const achievementIcons = { star: Star, medal: Medal, trophy: Trophy, crown: Crown, camera: Camera, chat: ChatCircle, ticket: Ticket };

const Gamification = () => {
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [challenges, setChallenges] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("prizes");
  const [loading, setLoading] = useState(true);
  const [instaLink, setInstaLink] = useState("");
  const [redeemCode, setRedeemCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPromoFS, setShowPromoFS] = useState(true);
  const h = { headers: getAuthHeaders(), withCredentials: true };

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/gamification/me`, h),
      axios.get(`${BACKEND_URL}/api/gamification/leaderboard`, h),
      axios.get(`${BACKEND_URL}/api/gamification/challenges`, h),
      axios.get(`${BACKEND_URL}/api/gamification/history`, h),
    ]).then(([me, lb, ch, hist]) => {
      setData(me.data); setLeaderboard(lb.data); setChallenges(ch.data); setHistory(hist.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const submitInstagram = async () => {
    if (!instaLink.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gamification/instagram`, { link: instaLink },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true });
      toast.success(res.data.message);
      setInstaLink("");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setSubmitting(false); }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gamification/redeem-code`, { code: redeemCode },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true });
      toast.success(res.data.message);
      setRedeemCode("");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-12 text-[#115E59]">Carregando...</div>;
  if (!data) return null;

  const myRank = leaderboard.findIndex((l) => l.user_id === user?.id) + 1;
  const progressToNext = ((data.total_points % 200) / 200) * 100;

  const tabs = [
    { id: "prizes", label: "Prêmios" },
    { id: "challenges", label: "Desafios" },
    { id: "ranking", label: "Ranking" },
    { id: "earn", label: "Ganhar Pontos" },
    { id: "conquistas", label: "Conquistas" },
  ];

  return (
    <div data-testid="gamification-tab">
      {/* Fullscreen promo on open */}
      <AnimatePresence>
        {showPromoFS && (
          <PromoFullscreen
            onClose={() => setShowPromoFS(false)}
            onGoToChallenges={() => { setShowPromoFS(false); setActiveTab("challenges"); }}
          />
        )}
      </AnimatePresence>

      {/* Prize Banner */}
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl overflow-hidden mb-6 relative" data-testid="prizes-banner">
        <img src={PRIZES_IMG} alt="Prêmios" className="w-full h-48 sm:h-56 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end p-6">
          <div>
            <p className="text-yellow-400 text-xs font-bold uppercase tracking-widest mb-1">Desafio Semanal</p>
            <h2 className="text-white text-2xl sm:text-3xl font-black">Ganhe Prêmios Incríveis!</h2>
            <p className="text-white/80 text-sm mt-1">iPhone 17 Pro Max, Moto Elétrica e Dinheiro</p>
          </div>
        </div>
      </motion.div>

      {/* Points card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#042F2E] to-[#115E59] rounded-2xl p-6 mb-6 text-white" data-testid="points-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white/60 text-xs uppercase tracking-wider font-bold">Seus Pontos</p>
            <p className="text-4xl font-black">{data.total_points}</p>
          </div>
          <div className="text-right">
            <div className="bg-[#FF5A26] rounded-full px-4 py-1.5 inline-flex items-center gap-1.5">
              <Lightning size={16} weight="fill" className="text-yellow-300" />
              <span className="font-bold">Nível {data.level}</span>
            </div>
            {myRank > 0 && <p className="text-white/50 text-xs mt-1">#{myRank} no ranking</p>}
          </div>
        </div>
        <div className="bg-white/20 rounded-full h-2.5 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${progressToNext}%` }}
            transition={{ duration: 1 }} className="bg-[#FF5A26] h-full rounded-full" />
        </div>
        <p className="text-white/50 text-xs mt-1.5">{200 - (data.total_points % 200)} pts para nível {data.level + 1}</p>
      </motion.div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-2 mb-6 pb-1 no-scrollbar">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all border-2 ${
              activeTab === t.id ? "bg-[#042F2E] text-white border-[#042F2E]" : "bg-white text-[#115E59] border-[#E4DFD3]"
            }`} data-testid={`gam-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* PRIZES */}
      {activeTab === "prizes" && challenges && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-[#042F2E]">Prêmios Semanais</h3>
          {[
            { rank: "1°", prize: "iPhone 17 Pro Max ou Moto Elétrica", color: "from-yellow-400 to-amber-500", icon: "🏆" },
            { rank: "2°", prize: "R$ 1.000,00", color: "from-gray-300 to-gray-400", icon: "🥈" },
            { rank: "3°", prize: "R$ 200,00", color: "from-orange-400 to-orange-600", icon: "🥉" },
          ].map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl border border-[#E4DFD3] p-4 flex items-center gap-4" data-testid={`prize-${i}`}>
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center text-white font-black text-xl shadow-md`}>
                {p.icon}
              </div>
              <div>
                <p className="font-bold text-[#042F2E] text-lg">{p.rank} Lugar</p>
                <p className="text-[#115E59] text-sm font-semibold">{p.prize}</p>
              </div>
            </motion.div>
          ))}
          <div className="bg-[#FFF4E6] rounded-xl p-4 text-sm text-[#042F2E]">
            <p className="font-bold mb-1">Como funciona?</p>
            <p className="text-[#115E59]">Acumule o máximo de pontos durante a semana. Os 4 primeiros no ranking ganham os prêmios. O ranking zera toda segunda-feira!</p>
          </div>
        </div>
      )}

      {/* CHALLENGES */}
      {activeTab === "challenges" && challenges && (
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-[#042F2E] mb-2">Desafios da Semana</h3>
          {challenges.challenges.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className={`bg-white rounded-xl border p-4 ${c.completed ? "border-[#10B981] bg-green-50" : "border-[#E4DFD3]"}`}
              data-testid={`challenge-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target size={18} weight={c.completed ? "fill" : "duotone"} className={c.completed ? "text-[#10B981]" : "text-[#FF5A26]"} />
                  <p className="font-bold text-[#042F2E] text-sm">{c.title}</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.completed ? "bg-[#10B981] text-white" : "bg-[#FFF4E6] text-[#FF5A26]"}`}>
                  {c.total_earned}/{c.max_total} pts
                </span>
              </div>
              <p className="text-xs text-[#115E59] mb-2">{c.desc}</p>
              <div className="bg-[#F4F1EA] rounded-full h-2 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${c.completed ? "bg-[#10B981]" : "bg-[#FF5A26]"}`}
                  style={{ width: `${Math.min(100, (c.progress / c.target) * 100)}%` }} />
              </div>
              <p className="text-[10px] text-[#115E59] mt-1">{c.progress}/{c.target}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* RANKING */}
      {activeTab === "ranking" && (
        <div className="bg-white rounded-2xl border border-[#E4DFD3] overflow-hidden">
          {leaderboard.length === 0 ? (
            <div className="text-center py-12"><Trophy size={48} weight="duotone" className="text-[#E4DFD3] mx-auto mb-3" /><p className="text-[#115E59]">Ranking vazio</p></div>
          ) : leaderboard.map((entry, i) => {
            const isMe = entry.user_id === user?.id;
            const medals = ["bg-yellow-400", "bg-gray-400", "bg-orange-600"];
            return (
              <div key={entry.user_id} className={`flex items-center gap-4 px-5 py-4 border-b border-[#E4DFD3] last:border-0 ${isMe ? "bg-[#FFF4E6]" : ""}`}
                data-testid={`rank-${entry.rank}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm shrink-0 ${i < 3 ? `${medals[i]} text-white` : "bg-[#F4F1EA] text-[#115E59]"}`}>
                  {entry.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-bold truncate ${isMe ? "text-[#FF5A26]" : "text-[#042F2E]"}`}>{entry.name} {isMe && "(Você)"}</p>
                  <p className="text-xs text-[#115E59]">Nível {entry.level}</p>
                </div>
                <p className="font-black text-[#FF5A26]">{entry.total_points}<span className="text-xs font-normal text-[#115E59] ml-1">pts</span></p>
              </div>
            );
          })}
        </div>
      )}

      {/* EARN POINTS */}
      {activeTab === "earn" && (
        <div className="space-y-6">
          {/* How to earn */}
          <div className="bg-white rounded-2xl border border-[#E4DFD3] p-5">
            <h3 className="font-bold text-[#042F2E] mb-4">Como ganhar pontos</h3>
            <div className="space-y-3">
              {[
                { label: "Visite o clube", pts: "+100/visita", color: "bg-green-100 text-green-700" },
                { label: "Compre ingresso", pts: "+100/compra", color: "bg-blue-100 text-blue-700" },
                { label: "Indicado compra Entrada", pts: "+40 p/ indicador", color: "bg-indigo-100 text-indigo-700" },
                { label: "Indicado compra Mensal", pts: "+360 p/ indicador", color: "bg-indigo-100 text-indigo-700" },
                { label: "Indicado compra Anual", pts: "+1920 p/ indicador", color: "bg-indigo-100 text-indigo-700" },
                { label: "Alguém usa seu código", pts: "+500 p/ você", color: "bg-purple-100 text-purple-700" },
                { label: "Complete desafios", pts: "+50~500", color: "bg-orange-100 text-orange-700" },
                { label: "Poste fotos na galeria", pts: "+100/foto", color: "bg-pink-100 text-pink-700" },
                { label: "Post no Instagram", pts: "+400/post", color: "bg-violet-100 text-violet-700" },
                { label: "Divulgue o app", pts: "+50/dia", color: "bg-sky-100 text-sky-700" },
                { label: "Confirme eventos", pts: "+50/dia", color: "bg-teal-100 text-teal-700" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between bg-[#F4F1EA] rounded-xl px-4 py-3">
                  <span className="text-sm text-[#042F2E] font-medium">{item.label}</span>
                  <span className={`${item.color} font-bold text-xs px-2.5 py-1 rounded-full`}>{item.pts}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Instagram submission */}
          <div className="bg-white rounded-2xl border border-[#E4DFD3] p-5">
            <div className="flex items-center gap-2 mb-3">
              <InstagramLogo size={22} weight="fill" className="text-[#E1306C]" />
              <h3 className="font-bold text-[#042F2E]">Post no Instagram</h3>
            </div>
            <p className="text-xs text-[#115E59] mb-3">Marque @marabuclub_ no seu post e cole o link aqui para ganhar <strong>+400 pontos</strong></p>
            <div className="flex gap-2">
              <Input value={instaLink} onChange={(e) => setInstaLink(e.target.value)} placeholder="https://instagram.com/p/..."
                className="rounded-xl border-[#E4DFD3] flex-1 text-sm" data-testid="insta-link-input" />
              <Button onClick={submitInstagram} disabled={submitting || !instaLink.trim()}
                className="bg-gradient-to-r from-[#E1306C] to-[#C13584] text-white rounded-xl shrink-0" data-testid="submit-insta-btn">
                Enviar
              </Button>
            </div>
          </div>

          {/* Redeem code */}
          <div className="bg-white rounded-2xl border border-[#E4DFD3] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Code size={22} weight="bold" className="text-[#FF5A26]" />
              <h3 className="font-bold text-[#042F2E]">Resgatar Código</h3>
            </div>
            <p className="text-xs text-[#115E59] mb-3">Tem um código de indicação? Cole aqui para vincular. Só quem te indicou ganha os pontos!</p>
            <div className="flex gap-2">
              <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="CÓDIGO"
                className="rounded-xl border-[#E4DFD3] flex-1 font-mono uppercase text-sm" data-testid="redeem-code-input" />
              <Button onClick={handleRedeem} disabled={submitting || !redeemCode.trim()}
                className="bg-[#FF5A26] hover:bg-[#E04818] text-white rounded-xl shrink-0" data-testid="redeem-btn">
                Resgatar
              </Button>
            </div>
          </div>

          {/* Share your code */}
          <div className="bg-gradient-to-br from-[#FF5A26] to-[#E04818] rounded-2xl p-5 text-white">
            <h3 className="font-bold mb-2">Seu código de indicação</h3>
            <div className="bg-white/20 rounded-xl p-3 flex items-center justify-between mb-3">
              <span className="font-mono font-bold text-lg">{user?.referral_code || "..."}</span>
              <Button onClick={() => { navigator.clipboard.writeText(user?.referral_code || ""); toast.success("Copiado!"); }}
                className="bg-white/30 text-white rounded-full px-3 py-1.5" data-testid="copy-my-code">
                <Copy size={16} className="mr-1" /> Copiar
              </Button>
            </div>
            <p className="text-white/80 text-xs mb-3">Quando alguém usa seu código, <strong>você ganha +500 pontos</strong> pela indicação! Cada pessoa só pode usar 1 código.</p>
            <Button onClick={() => {
              const msg = `Venha pro MARABU OPEN CLUB! Use meu código ${user?.referral_code} no app e ganhe pontos!\n${window.location.origin}/login?tab=register&ref=${user?.referral_code}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
            }} className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold rounded-full" data-testid="share-code-wa">
              <WhatsappLogo size={20} weight="fill" className="mr-2" /> Compartilhar no WhatsApp
            </Button>
          </div>
        </div>
      )}

      {/* CONQUISTAS */}
      {activeTab === "conquistas" && (
        <div>
          <h3 className="font-bold text-[#042F2E] mb-4">Conquistas ({data.unlocked_count}/{data.total_achievements})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.achievements.map((a, i) => {
              const Icon = achievementIcons[a.icon] || Star;
              return (
                <motion.div key={a.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                  className={`rounded-2xl border p-4 text-center ${a.unlocked ? "bg-white border-[#FF5A26] shadow-md" : "bg-[#F4F1EA] border-[#E4DFD3] opacity-50"}`}
                  data-testid={`achievement-${a.id}`}>
                  <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${a.unlocked ? "bg-[#FF5A26]" : "bg-[#E4DFD3]"}`}>
                    <Icon size={24} weight="fill" className={a.unlocked ? "text-white" : "text-[#115E59]"} />
                  </div>
                  <p className="font-bold text-xs text-[#042F2E]">{a.name}</p>
                  <p className="text-[10px] text-[#115E59] mt-0.5">{a.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gamification;
